/**
 * Sample flight data feeds, for the demonstration without a platform: a SITA
 * gateway pushing AIDX and live, an airline's Type B mailbox gone quiet, and
 * a handler's JSON service failing on a secret nobody has set — the three
 * states the screen has to be able to say. Every name says "sample", and the
 * list comes back marked `sample`, so nothing here passes for a connection.
 */
import type { FeedMessage, FlightFeed } from "../domain/flightFeeds";

const iso = (now: number, minAgo: number) => new Date(now - minAgo * 60_000).toISOString();
const hm = (now: number, minAgo: number) => iso(now, minAgo).slice(11, 16).replace(":", "");
const dd = (now: number) => iso(now, 0).slice(8, 10);

export function mockFeeds(now: number = Date.now()): FlightFeed[] {
  return [
    {
      id: "feed-sita",
      key: "sita-aidx",
      name: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      transport: "push",
      enabled: true,
      config: { quietMinutes: 30, createUnknown: false, secretRefs: {} },
      secrets: {},
      pushPath: "/v1/aodb/aidx?feed=sita-aidx",
      status: {
        state: "live",
        lastMessageAt: iso(now, 2),
        lagSeconds: 120,
        sourceLagSeconds: 4,
        perHour: 46,
        last24h: 812,
        outcomes24h: { taken: 771, held: 12, stored: 0, duplicate: 23, refused: 6 },
        refusals24h: [
          { reason: "not well-formed XML", count: 4 },
          { reason: "the leg is JED→RUH, not this airport (MED, OEMA)", count: 2 },
        ],
      },
    },
    {
      id: "feed-typeb",
      key: "sv-typeb-mail",
      name: "Saudia Type B by email (sample)",
      kind: "typeb",
      transport: "imap",
      enabled: true,
      config: {
        imap: { host: "mail.example.aero", port: 993, user: "medkl-typeb@example.aero", folder: "INBOX" },
        secretRefs: { password: "SLT_FEED_SV_MAILBOX" },
        pollSeconds: 60,
        quietMinutes: 30,
      },
      secrets: { password: { name: "SLT_FEED_SV_MAILBOX", set: true } },
      lastPollAt: iso(now, 1),
      lastOkAt: iso(now, 1),
      status: {
        state: "quiet",
        why: "nothing for 52 min (quiet after 30)",
        lastMessageAt: iso(now, 52),
        lagSeconds: 52 * 60,
        sourceLagSeconds: 38,
        perHour: 0,
        last24h: 164,
        outcomes24h: { taken: 149, held: 4, stored: 7, duplicate: 3, refused: 1 },
        refusals24h: [{ reason: "no message type where the message begins (found 'M#T 1483/2 HZ??KA')", count: 1 }],
      },
    },
    {
      id: "feed-json",
      key: "handler-json",
      name: "Ground handler's flight API — JSON (sample)",
      kind: "json",
      transport: "https",
      enabled: true,
      config: {
        url: "https://api.handler.example.aero/v2/flights",
        cursorParam: "since",
        auth: { kind: "oauth", tokenUrl: "https://api.handler.example.aero/oauth/token", clientId: "sky-logics" },
        secretRefs: { client_secret: "SLT_FEED_HANDLER_SECRET" },
        mapping: {
          records: "data.flights",
          id: "messageId",
          cursor: "sequence",
          fields: {
            airline: "carrier",
            flight_number: "flightNo",
            origin_date: "date",
            departure_airport: "from",
            arrival_airport: "to",
            position: "stand",
            EIBT: "times.eibt",
          },
        },
        pollSeconds: 60,
        quietMinutes: 30,
      },
      secrets: { client_secret: { name: "SLT_FEED_HANDLER_SECRET", set: false } },
      lastPollAt: iso(now, 1),
      lastOkAt: iso(now, 190),
      lastError: "the OAuth client id or secret is not set in this deployment",
      status: {
        state: "failing",
        why: "the last fetch failed: the OAuth client id or secret is not set in this deployment",
        lastMessageAt: iso(now, 190),
        lagSeconds: 190 * 60,
        perHour: 0,
        last24h: 38,
        outcomes24h: { taken: 38, held: 0, stored: 0, duplicate: 0, refused: 0 },
        refusals24h: [],
      },
    },
  ];
}

export function mockFeedMessages(now: number = Date.now()): FeedMessage[] {
  const d = dd(now);
  const aidx = (id: string, flight: string, body: string) =>
    `<IATA_AIDX_FlightLegNotifRQ TimeStamp="${iso(now, 2)}" TransactionIdentifier="${id}">\n  <FlightLeg>\n    <LegIdentifier><Airline>SV</Airline><FlightNumber>${flight}</FlightNumber>…</LegIdentifier>\n    <LegData>${body}</LegData>\n  </FlightLeg>\n</IATA_AIDX_FlightLegNotifRQ>`;
  return [
    {
      id: "fm-1",
      feedId: "feed-sita",
      feed: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      channel: "push",
      outcome: "taken",
      raw: aidx("SITA-0412", "1483", '<OperationTime OperationQualifier="ONB" TimeType="ACT">…</OperationTime>'),
      ignored: [],
      changes: ["milestones", "actual_at", "status", "handling"],
      sentAt: iso(now, 2),
      receivedAt: iso(now, 2),
    },
    {
      id: "fm-2",
      feedId: "feed-sita",
      feed: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      channel: "push",
      outcome: "taken",
      reason: undefined,
      raw: aidx(
        "SITA-0411",
        "1522",
        "<AirportResources><Resource><AircraftParkingPosition>111</AircraftParkingPosition>…",
      ),
      ignored: ["FlightLeg/LegData/VendorTurnaroundScore"],
      changes: ["handling"],
      sentAt: iso(now, 6),
      receivedAt: iso(now, 6),
    },
    {
      id: "fm-3",
      feedId: "feed-sita",
      feed: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      channel: "push",
      outcome: "held",
      reason: "no XY2210 arrival on the record around " + iso(now, 0).slice(0, 10) + " — held, not invented",
      raw: aidx("SITA-0409", "2210", "<OperationalStatus>SCH</OperationalStatus>…"),
      ignored: [],
      changes: [],
      sentAt: iso(now, 11),
      receivedAt: iso(now, 11),
    },
    {
      id: "fm-4",
      feedId: "feed-sita",
      feed: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      channel: "push",
      outcome: "duplicate",
      reason: "already received (taken)",
      raw: aidx("SITA-0404", "1483", "…"),
      ignored: [],
      changes: [],
      duplicateOf: "fm-1",
      sentAt: iso(now, 14),
      receivedAt: iso(now, 14),
    },
    {
      id: "fm-5",
      feedId: "feed-sita",
      feed: "SITA AirportConnect — AIDX (sample)",
      kind: "aidx",
      channel: "push",
      outcome: "refused",
      reason: "not well-formed XML: mismatched tag: line 7, column 4",
      raw: "<IATA_AIDX_FlightLegNotifRQ><FlightLeg><LegIdentifier></FlightLeg>",
      ignored: [],
      changes: [],
      receivedAt: iso(now, 19),
    },
    {
      id: "fm-6",
      feedId: "feed-typeb",
      feed: "Saudia Type B by email (sample)",
      kind: "mvt",
      channel: "imap",
      outcome: "taken",
      raw: `QU MEDKLSV MEDOPXH\n.JEDKKSV ${d}${hm(now, 52)}\nMVT\nSV1466/${d}.HZAKB.JED\nAD${hm(now, 60)}/${hm(now, 53)} EA${hm(now, 5)} MED\nDL81/0025`,
      envelope: { priority: "QU", addresses: ["MEDKLSV", "MEDOPXH"], originator: "JEDKKSV" },
      ignored: [],
      changes: ["estimated_at", "status"],
      sentAt: iso(now, 52),
      receivedAt: iso(now, 52),
    },
    {
      id: "fm-7",
      feedId: "feed-typeb",
      feed: "Saudia Type B by email (sample)",
      kind: "ldm",
      channel: "imap",
      outcome: "taken",
      raw: `QU MEDKLSV\n.JEDKKSV ${d}${hm(now, 55)}\nLDM\nSV1466/${d}.HZAKB.J20Y154.2/6\n-MED.140/6/2.T2450.1/1200.2/800.3/450.PAX/12/134.PAD/0/0.B112/1680`,
      envelope: { priority: "QU", addresses: ["MEDKLSV"], originator: "JEDKKSV" },
      ignored: [],
      changes: ["loads", "capacity"],
      sentAt: iso(now, 55),
      receivedAt: iso(now, 55),
    },
    {
      id: "fm-8",
      feedId: "feed-typeb",
      feed: "Saudia Type B by email (sample)",
      kind: "ssm",
      channel: "imap",
      outcome: "stored",
      reason:
        "SSM CNL is kept for a person to apply to the seasonal schedule; a feed adds new lines (SSM NEW) but does not rewrite or remove them",
      raw: "QU MEDKLSV\n.JEDSCSV 280610\nSSM\nUTC\nCNL\nSV1471\n01NOV26 28MAR27 246",
      ignored: [],
      changes: [],
      receivedAt: iso(now, 70),
    },
    {
      id: "fm-9",
      feedId: "feed-typeb",
      feed: "Saudia Type B by email (sample)",
      kind: "unknown",
      channel: "imap",
      outcome: "refused",
      reason:
        "no message type where the message begins (found 'M#T 1483/2 HZ??KA'); expected MVT, LDM, ASM or SSM after the address and dot-lines — garbled, or not Type B",
      raw: `QU MEDKLSV\n.JEDKKSV ${d}0930\nM#T 1483/2 HZ??KA\nA)0605/06!2 E?0705`,
      ignored: [],
      changes: [],
      receivedAt: iso(now, 95),
    },
  ];
}
